import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

type Theme = 'light' | 'dark';
type ThemeMode = Theme | 'system';

interface ThemeContextType {
  /** Effective theme after applying system preference (if mode === 'system') */
  theme: Theme;
  /** User-selected mode */
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'theme';

function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // ignore
  }
  return 'system';
}

function getPrefersDark(): boolean {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => readStoredMode());
  const [prefersDark, setPrefersDark] = useState<boolean>(() => getPrefersDark());

  // Keep prefers-color-scheme in sync when in "system" mode.
  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      mq = null;
    }

    if (!mq) return;

    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      // Some older browsers call the listener with the MediaQueryList itself.
      const matches = 'matches' in e ? e.matches : false;
      setPrefersDark(matches);
    };

    // Safari 13/old Chromium - use type assertion for legacy API
    const anyMq = mq as MediaQueryList & { addListener?: (cb: typeof handler) => void; removeListener?: (cb: typeof handler) => void };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    else if (typeof anyMq.addListener === 'function') anyMq.addListener(handler);

    return () => {
      if (typeof mq?.removeEventListener === 'function') mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
      else if (anyMq.removeListener) anyMq.removeListener(handler);
    };
  }, []);

  const isDark = useMemo(() => mode === 'dark' || (mode === 'system' && prefersDark), [mode, prefersDark]);
  const theme: Theme = isDark ? 'dark' : 'light';

  // Apply the class to <html> so Tailwind `dark:` utilities work.
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add('dark');
    else root.classList.remove('dark');
    root.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  // Persist mode (including "system").
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, mode, setMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
